/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { SettingsNativeBridgeFactory } from '../bridge/SettingsNativeBridgeFactory';
import { NativePlatformType, SettingsEntryType } from '../Contracts/base';
import {
	SettingsLogger as Logger,
	AotCategory,
	logConstants,
} from '../utils/SettingsLogger';
import { SettingsCategories, ISettingCategory } from '../config/SettingsCategories';
import {
	getCategoriesForPlatformEntry,
	isPlatformEntrypointSupported,
} from '../config/SettingsPlatformConfig';
import { SettingsCategory } from '../config/SettingsCategory';

// Context interface
interface ISettingsConfigContext {
	error: string | null;
	settingsCategories: ISettingCategory[];
	entrypoint?: SettingsEntryType | null;
}

const defaultSettingsConfigContext: ISettingsConfigContext = {
	error: null,
	settingsCategories: [],
	entrypoint: null,
};

// Create context
export const SettingsConfigContext = React.createContext<ISettingsConfigContext>(
	defaultSettingsConfigContext
);

// Hook to use the context
export const useSettingsConfig = (): ISettingsConfigContext => {
	return React.useContext<ISettingsConfigContext>(SettingsConfigContext);
};

// Global window type extension for Settings UI
declare global {
	interface Window {
		SettingsUI?: {
			loadSettings: (category?: SettingsCategory) => void;
			showError: (message: string) => void;
			clearError: () => void;
		};
	}
}

export const SettingsConfigProvider = (props: {
	children?: React.ReactNode;
	categories?: ISettingCategory[];
}): JSX.Element => {
	const bridge = SettingsNativeBridgeFactory.getBridge();
	const [error, setError] = useState<string | null>(null);
	const [settingsCategories, setSettingsCategories] = useState<ISettingCategory[]>(
		props.categories || SettingsCategories
	);
	const [entrypoint, setEntrypoint] = useState<SettingsEntryType | null>(null);

	// Update categories when bridge is initialized
	useEffect(() => {
		const updateCategoriesForPlatformAndEntry = (
			platform: NativePlatformType,
			settingsEntrypoint: string
		) => {
			const allCategories = props.categories || SettingsCategories;

			const entrypoint = settingsEntrypoint as SettingsEntryType;

			// Validate if platform supports this entrypoint
			if (!isPlatformEntrypointSupported(platform, entrypoint)) {
				const errorMessage = `Platform '${platform}' does not support entrypoint '${entrypoint}'`;
				Logger.getInstance().write(
					logConstants.ERROR,
					errorMessage,
					AotCategory.Error,
					errorMessage
				);
				setError(errorMessage);
				return;
			}

			// Get valid category IDs from PlatformSettingsMap
			const validCategoryIds = getCategoriesForPlatformEntry(platform, entrypoint);
			const validCategoryIdStrings = validCategoryIds.map((cat: SettingsCategory) =>
				cat.valueOf()
			);

			// Filter allCategories to only include categories that are valid for this platform + entrypoint
			// Sort by the order they appear in PLATFORM_CATEGORY_MAPPINGS
			const filteredCategories = allCategories
				.filter(
					(category) =>
						category.isEnabled !== false && validCategoryIdStrings.includes(category.id)
				)
				.sort((a, b) => {
					const indexA = validCategoryIdStrings.indexOf(a.id);
					const indexB = validCategoryIdStrings.indexOf(b.id);
					return indexA - indexB;
				});

			// Update state with filtered and sorted categories
			setSettingsCategories(filteredCategories);
			setEntrypoint(entrypoint);

			Logger.getInstance().write(
				logConstants.DEBUG,
				`Filtered settings categories for platform: ${platform}, entry: ${settingsEntrypoint} (${filteredCategories.length} categories)`,
				AotCategory.Info,
				`Settings categories loaded for platform: ${platform}, entry: ${settingsEntrypoint} (${
					filteredCategories.length
				} categories: ${filteredCategories.map((c) => c.id).join(', ')})`
			);
		};

		// Register callback to update when bridge is initialized
		// Callback receives initializeParams with platform and settingsEntrypoint
		const cleanup = bridge.onInitialized((initializeParams) => {
			updateCategoriesForPlatformAndEntry(
				initializeParams.platform,
				initializeParams.settingsEntrypoint
			);
		});

		return cleanup;
	}, [bridge, props.categories]);

	// Expose functions to be called from native
	useEffect(() => {
		// Register callbacks for native communication
		window.SettingsUI = {
			loadSettings: () => {
				// Individual pages handle their own loading
				// This is kept for backward compatibility with native calls
			},
			showError: (message: string) => {
				setError(message);
			},
			clearError: () => {
				setError(null);
			},
		};

		// Notify native that Settings UI is ready
		if (bridge && typeof bridge.settingsReady === 'function') {
			bridge.settingsReady();
		}

		return () => {
			// Cleanup
			delete window.SettingsUI;
		};
	}, [bridge]);

	const contextValue = {
		error,
		settingsCategories,
		entrypoint,
	};

	return (
		<SettingsConfigContext.Provider value={contextValue}>
			{props.children}
		</SettingsConfigContext.Provider>
	);
};
